import { Component, inject, Inject, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CtAttributesService } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { CtMetadata } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-metadata';
import { DashboardCartAndSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/dashboard-cart-and-settings';
import {
  GridWidgetSettings,
  IWidgetSettings,
} from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/widget-settings';
import { AttributeShortInfo } from '@cloud-twin-v2/ct-map/models/attribute-short-info';
import { CTMapDashboardBridgeService } from '@cloud-twin-v2/ct-map/services/ct-map-dashboard-bridge.service';
import { CTResultsFormatService } from '@cloud-twin-v2/ct-map/services/ct-results-format.service';
import { AttributeInfoDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout.tokens';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { DashboardChildWidgetParams } from '@common-modules/right-panel/dashboards/dashboard-widget-params';
import { GridSetting } from '@common-modules/shared/constants/grid.constants';
import { GridConfiguration } from '@common-modules/shared/grid/grid-configuration';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { IFiltrableItemDto } from '@common-modules/shared/model/filtrable-items/filtrable-item.dto';
import { plainToClass } from 'class-transformer';
import { take } from 'rxjs';
import { BaseDashboardWidgetComponent } from '../core/base-dashboard-widget-component';

@Component({
  selector: 'ct-dashboard-grid-widget',
  templateUrl: './dashboard-grid-widget.component.html',
  styleUrl: './dashboard-grid-widget.component.scss',
  standalone: false,
})
export class DashboardGridWidgetComponent extends BaseDashboardWidgetComponent {
  private _currentWidgetSettings: StateWidgetSettings;
  private readonly _ctAttributeService = inject(CtAttributesService);

  gridConfiguration: GridConfiguration;

  metadata: CtMetadata = {};
  attributes: AttributeInfoDto[] = [];
  attributesByFiltrableType: AttributeShortInfo[] = [];
  gridSetting: GridSetting;
  neTypes: string[];
  _previusWidgetSettings: IWidgetSettings;
  additionalColumnsByScenario: { [scenarioId: string]: { [property: string]: string }[] }[] = [];
  data: { [property: string]: string }[];

  connectionColumns = [
    {
      field: 'modelName',
      visible: true,
      title: 'Model',
      width: 150,
      type: 'text',
    },
    {
      field: 'modelVersionName',
      visible: true,
      title: 'Model Version',
      width: 150,
      type: 'text',
    },
    {
      field: 'scenarioName',
      visible: true,
      title: 'Scenario',
      width: 150,
      type: 'text',
    },
  ];

  private readonly _columnTypeAttribute = 'attribute';
  private readonly _columnTypeInternal = 'internal';
  private readonly _columnTypeExportOnly = 'export-only';

  constructor(
    injector: Injector,
    private readonly _resultsFormatService: CTResultsFormatService,
    private readonly _attributeResultsBridge: CTMapDashboardBridgeService,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings
  ) {
    super(injector, widgetSettings);
    this._currentWidgetSettings = widgetSettings;
    this._ctAttributeService.metadata$.pipe(take(1)).subscribe((metadata) => {
      this.metadata = metadata;
    });

    this.getAttributesByMetadata();
    this.setGridConfiguration(widgetSettings.params);
  }

  get componentName(): string {
    return 'DashboardGridWidgetComponent';
  }

  onWidgetInit(): void {
    this.listenSettingsChange();
    this.gridSetting = this.buildDynamicGridSetting();

    this.gridDataChange();
  }

  onGridConfigurationChange(gridConfiguration: GridConfiguration) {
    const setting = globalUtilsHelper.clone(
      this._widgetSettings.itemSettings?.params
    ) as DashboardCartAndSettings;
    const gridWidgetSettings = new GridWidgetSettings();
    gridWidgetSettings.gridConfiguration = gridConfiguration;
    setting.widgetSettings = gridWidgetSettings;

    this.updateItemSettings({
      title: this.gridConfiguration.gridName ?? this._settings.title,
      params: setting,
      widgetInstanceKey: this.getWidgetInstanceKey(),
    });

    this.gridDataChange();
  }

  reload(updatedParams: DashboardCartAndSettings) {
    if (
      updatedParams.widgetSettings &&
      'gridConfiguration' in updatedParams.widgetSettings &&
      updatedParams.widgetSettings.gridConfiguration
    ) {
      const config = updatedParams.widgetSettings.gridConfiguration as GridConfiguration;

      const widgetSettings = new GridWidgetSettings();
      widgetSettings.gridConfiguration = config;
      updatedParams.widgetSettings = widgetSettings;
      if (this._widgetSettings.itemSettings) {
        this._widgetSettings.itemSettings.params = updatedParams;
      }
      this.setGridConfiguration(updatedParams);
    }
  }

  setGridConfiguration(params: DashboardChildWidgetParams) {
    if (!params) {
      return;
    }

    const dashboardCart: DashboardCartAndSettings = plainToClass(DashboardCartAndSettings, params);

    const { start, end } = this.getIntervalDates(new Date(dashboardCart.timeStep));

    dashboardCart.items.forEach((item) => {
      item.startDate = start;
      item.endDate = end;
    });

    dashboardCart.visibleItems.forEach((item) => {
      item.startDate = start;
      item.endDate = end;
    });

    if (dashboardCart.visibleItems && this.attributes) {
      this.getAttributesByFiltrableType(dashboardCart.visibleItems);
    }

    if (dashboardCart.widgetSettings instanceof GridWidgetSettings) {
      const gridConfig = (dashboardCart.widgetSettings as GridWidgetSettings).gridConfiguration;
      this.gridConfiguration = gridConfig;
    } else {
      if (!this.gridConfiguration) {
        this.gridConfiguration = new GridConfiguration();
      }
      this.gridConfiguration.gridName = this._settings.title;
    }
  }

  getAttributesByMetadata(): void {
    this.attributes = [];
    for (const scenarioId in this.metadata) {
      const categoriesArr = this.metadata[scenarioId];
      for (const cat of categoriesArr) {
        if (cat.categories) {
          for (const category of cat.categories) {
            if (category.attributes) {
              this.attributes.push(...category.attributes);
            }
          }
        }
      }
    }
  }

  getAttributesByFiltrableType(visibleItems: IFiltrableItemDto[]): void {
    this.attributesByFiltrableType = (visibleItems as any)
      .map((item) => {
        const attr = this.attributes.find((attr) => attr.name === item.filtrableType.attributeId);
        const scenarioId = item.filtrableType.workspaceConnectSetting.scenarioId;
        if (attr) {
          return {
            field: attr.name,
            alias: attr.alias,
            units: attr.units,
            format: attr.format,
            scenarioId: scenarioId,
            networkElementId: item.filtrableType.networkElementId,
            attributeTypeKey: attr.attributeTypeKey,
          } as AttributeShortInfo;
        }
        return undefined;
      })
      .filter((attr) => !!attr);
  }

  buildDynamicGridSetting(): any {
    const gridSettings: any = {
      count: true,
      filterable: 'menu',
      navigatable: false,
      pageSize: 20,
      ignoreCase: true,
      pageable: {
        type: 'input',
        pageSizes: [10, 20, 50],
      },
      reorderable: true,
      resizable: true,
      selectable: {
        enabled: true,
        mode: 'single',
        cell: true,
        drag: false,
      },
      showSelectAllColumn: false,
      showZoneColumns: false,
      sortable: true,
      useUoM: false,
      gridColumnSettings: [
        {
          field: 'scenarioId',
          visible: false,
          title: 'scenarioId',
          width: 150,
          type: 'text',
        },
        {
          field: 'networkElementId',
          visible: false,
          title: 'networkElementId',
          width: 150,
          type: 'text',
        },
        {
          field: 'networkElementName',
          visible: true,
          title: 'Network Element Name',
          width: 150,
          type: 'text',
        },
      ],
      selectByFieldName: 'networkElementId',
      buttons: {
        show: ['clear_grid_filters', 'export_excel'],
      },
      sort: [
        {
          field: 'networkElementName',
          dir: 'desc',
        },
      ],
      excelFileName: '',
      selectionPersistency: false,
      hideHeader: false,
      utcDates: false,
    };

    if (this.attributesByFiltrableType && this.attributesByFiltrableType.length > 0) {
      this.attributesByFiltrableType.forEach((attribute: AttributeShortInfo) => {
        const fieldName = attribute.field || '';
        const titleName = attribute.alias || '';

        if (fieldName) {
          const columnExists = gridSettings.gridColumnSettings.some(
            (column: any) => column.field === fieldName
          );

          if (!columnExists) {
            let columns = [];
            switch (attribute.attributeTypeKey) {
              case 'Double':
                columns = this.getDoubleColumns(attribute);
                break;

              case 'Boolean':
                columns = this.getCommonColumn(fieldName, titleName, 'boolean');
                break;

              default:
                columns = this.getCommonColumn(fieldName, titleName);
                break;
            }

            gridSettings.gridColumnSettings = [...gridSettings.gridColumnSettings, ...columns];
          }
        }
      });
    }

    gridSettings.gridColumnSettings = [
      ...gridSettings.gridColumnSettings,
      ...this.connectionColumns,
    ];

    return gridSettings;
  }

  getDoubleColumns(attribute: AttributeShortInfo): any[] {
    const fieldName = attribute.field;
    const titleName = attribute.alias;
    const formatFieldName = `${fieldName}_format`;
    const unitFieldName = `${fieldName}_units`;

    var columns = this.getCommonColumn(fieldName, titleName, this._columnTypeAttribute);

    if (attribute?.format) {
      columns = [
        ...columns,
        ...this.getCommonColumn(formatFieldName, titleName, this._columnTypeInternal, false),
      ];
      this.insertColumnMetadata(attribute.scenarioId, formatFieldName, attribute.format);
    }

    if (attribute?.units) {
      columns = [
        ...columns,
        ...this.getCommonColumn(
          unitFieldName,
          `${titleName} [Units]`,
          this._columnTypeExportOnly,
          false,
          fieldName
        ),
      ];
      const unitValue = attribute.units == '-' ? '' : attribute.units;
      this.insertColumnMetadata(attribute.scenarioId, unitFieldName, unitValue);
    }

    return columns;
  }

  insertColumnMetadata(scenarioId: string, fieldName: string, value: string) {
    if (!this.additionalColumnsByScenario[scenarioId]) {
      this.additionalColumnsByScenario[scenarioId] = [];
    }

    const metadataColumn: { [additionalField: string]: string } = {};

    metadataColumn[fieldName] = value ?? '';

    this.additionalColumnsByScenario[scenarioId].push(metadataColumn);
  }

  getCommonColumn(
    fieldName: string,
    titleName: string,
    type: string = 'text',
    visible: boolean = true,
    referenceColumn = null
  ) {
    const column = {
      field: fieldName,
      visible: visible,
      title: titleName,
      width: 150,
      type: type,
    };

    if (referenceColumn) {
      column['referenceColumn'] = referenceColumn;
    }

    return [column];
  }

  private listenGridData(value: any, attributes: any[]): void {
    if (value) {
      const networkElementIds = attributes
        .map((attr) => attr.networkElementId)
        .filter((id, idx, arr) => id && arr.indexOf(id) === idx);

      let dataFilteredByType: any[] = [];
      value.forEach((entry: any) => {
        if (Array.isArray(entry.values)) {
          const filtered = entry.values.filter((v: any) =>
            networkElementIds.includes(v.networkElementId)
          );
          dataFilteredByType = dataFilteredByType.concat(filtered);
        }
      });

      const dataWithAdditionalColumnInfo =
        this._resultsFormatService.mergeDataWithAdditionalColumns(
          dataFilteredByType,
          this.additionalColumnsByScenario
        );

      const attributeColumns = this.gridSetting.gridColumnSettings
        .filter((x) => x.type === this._columnTypeAttribute)
        ?.map((x) => x.field);

      if (attributeColumns && attributeColumns.length) {
        dataWithAdditionalColumnInfo.forEach((row) => {
          attributeColumns.forEach((field) => {
            if (row[field] == 'NaN') {
              row[field] = null;
            }
          });
        });
      }

      this.data = dataWithAdditionalColumnInfo;
    }
  }

  private gridDataChange() {
    this._attributeResultsBridge.attributeResults$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        this.listenGridData(value, this.attributesByFiltrableType);
      });
  }

  private getIntervalDates(step: Date) {
    const start = new Date(step);
    const end = new Date(step);
    start.setDate(step.getDate() - 7);
    end.setDate(step.getDate() + 7);
    return { start, end };
  }
}
